import { useEffect, useRef, useState } from 'react';
import type { CardSavePhase } from '../components/tenant/CardSaveBadge';
import type { AutosaveStatus } from './useAutosave';

/**
 * Which cards should show a spinner. A card is saving while its fields differ
 * from the last successful save. Only those cards show Saved when the request finishes.
 */
export function useCardSaveBadges<K extends string>(
  slices: Record<K, string>,
  status: AutosaveStatus,
  ready: boolean,
  resetKey: string | null,
  acked?: Record<K, string> | null,
): Record<K, CardSavePhase> {
  const baselineRef = useRef<Record<K, string> | null>(null);
  const resetRef = useRef<string | null | undefined>(undefined);
  const statusRef = useRef<AutosaveStatus>(status);
  const slicesRef = useRef(slices);
  const inflightRef = useRef<Partial<Record<K, string>>>({});
  const failedRef = useRef<Partial<Record<K, string>>>({});
  const [savedKeys, setSavedKeys] = useState<K[]>([]);
  const [savedForKey, setSavedForKey] = useState<string | null>(resetKey);
  const [baselineVersion, setBaselineVersion] = useState(0);
  if (savedForKey !== resetKey) {
    setSavedForKey(resetKey);
    setSavedKeys([]);
  }
  slicesRef.current = slices;

  const ackedKey = acked ? JSON.stringify(acked) : null;
  const ackedRef = useRef(acked);
  ackedRef.current = acked;
  if (resetRef.current !== resetKey) {
    resetRef.current = resetKey;
    baselineRef.current = acked ? { ...acked } : null;
    inflightRef.current = {};
    failedRef.current = {};
  }
  if (ready && baselineRef.current == null) {
    baselineRef.current = { ...slices };
  }

  useEffect(() => {
    const next = ackedRef.current;
    if (!next) return;
    const current = baselineRef.current;
    const same = current != null && (Object.keys(next) as K[]).every((key) => current[key] === next[key]);
    if (same) return;
    baselineRef.current = { ...next };
    setBaselineVersion((version) => version + 1);
  }, [ackedKey]);

  useEffect(() => {
    const previous = statusRef.current;
    statusRef.current = status;
    const baseline = baselineRef.current;
    if (!baseline) return;

    if (status === 'saving' && previous !== 'saving') {
      const current = slicesRef.current;
      const inflight: Partial<Record<K, string>> = {};
      for (const key of Object.keys(current) as K[]) {
        if (current[key] !== baseline[key]) inflight[key] = current[key];
      }
      inflightRef.current = inflight;
      failedRef.current = {};
      return;
    }

    if (status === 'saved' && previous === 'saving') {
      const inflight = inflightRef.current;
      const done = Object.keys(inflight) as K[];
      inflightRef.current = {};
      if (done.length === 0) return;
      const next = { ...baseline };
      for (const key of done) {
        const savedSlice = inflight[key];
        if (savedSlice !== undefined) next[key] = savedSlice;
      }
      baselineRef.current = next;
      setSavedKeys(done);
      setBaselineVersion((version) => version + 1);
      return;
    }

    if (status === 'error' && previous !== 'error') {
      failedRef.current = { ...slicesRef.current };
      inflightRef.current = {};
    }
  }, [status]);

  const baseline = baselineRef.current;
  const phases = {} as Record<K, CardSavePhase>;
  for (const key of Object.keys(slices) as K[]) {
    const dirty = baseline != null && slices[key] !== baseline[key];
    const failedHere = status === 'error' && failedRef.current[key] === slices[key];
    if (dirty && !failedHere) phases[key] = 'saving';
    else if (savedKeys.includes(key)) phases[key] = 'saved';
    else phases[key] = 'idle';
  }
  void baselineVersion;
  return phases;
}
